import ThemedCapture from "@/components/marketing/ThemedCapture";

/* ──────────────────────────────────────────────────────────
   ProductPanel — a real in-app screenshot presented as a
   branded, CSS-3D-tilted card: hairline border, soft meadow
   edge glow, and a gentle straighten-on-hover parallax
   (disabled under prefers-reduced-motion).

   Server-safe (no hooks) so it drops into both server and
   client marketing components. The tilt/hover rules ship
   inline with the component so it also works on routes that
   never import landing.css.

   The captures live in /public/product/sidebar-*.png —
   590×1568 shots of the live app sidebar for Laurierstraat
   218-1, Amsterdam, one per theme (ThemedCapture picks the
   `-light` twin on the light theme); the frame is the app's
   own surface colour so the seam vanishes on either ground.
   Re-cut both sets with scripts/capture-product-sidebar.ts.
────────────────────────────────────────────────────────── */

const PANEL_CSS = `
.pp-card {
  transform: perspective(1100px) rotateY(-9deg) rotateX(1.5deg);
  transition: transform 0.5s cubic-bezier(0.25, 0.1, 0.25, 1), box-shadow 0.5s ease;
  box-shadow: 0 28px 60px -28px rgba(0, 0, 0, 0.65), 0 0 38px -12px rgba(76, 175, 122, 0.30);
}
.pp-card.pp-face-r {
  transform: perspective(1100px) rotateY(9deg) rotateX(1.5deg);
}
@media (hover: hover) and (prefers-reduced-motion: no-preference) {
  .pp-card:hover {
    transform: perspective(1100px) rotateY(-2deg) rotateX(0.5deg);
    box-shadow: 0 28px 60px -24px rgba(0, 0, 0, 0.65), 0 0 46px -10px rgba(76, 175, 122, 0.42);
  }
  .pp-card.pp-face-r:hover {
    transform: perspective(1100px) rotateY(2deg) rotateX(0.5deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .pp-card {
    transition: none;
  }
}
`;

export type ProductPanelProps = {
  /** Path under /public, e.g. "/product/sidebar-overview.png". */
  src: string;
  alt: string;
  /** Short mono caption rendered under the card. */
  caption?: string;
  /** Which way the panel faces. Panels placed to the right of copy
      should face "left" (default) so they turn toward the text. */
  face?: "left" | "right";
  /** next/image responsive sizes — defaults to the typical column width. */
  sizes?: string;
  /** Applied to the outer figure; use it to control the panel width. */
  className?: string;
};

export default function ProductPanel({
  src,
  alt,
  caption,
  face = "left",
  sizes = "(min-width: 768px) 280px, 72vw",
  className = "",
}: ProductPanelProps) {
  return (
    // Follows the page theme: the capture inside is the matching-theme shot,
    // so the mat is simply the app's surface token.
    <figure className={`m-0 ${className}`}>
      <style>{PANEL_CSS}</style>
      <div
        className={`pp-card rounded-xl overflow-hidden bg-surface border ${
          face === "right" ? "pp-face-r" : ""
        }`}
        style={{ borderColor: "var(--hairline)" }}
      >
        <ThemedCapture
          src={src}
          alt={alt}
          width={590}
          height={1568}
          sizes={sizes}
          className="block w-full h-auto"
        />
      </div>
      {caption && (
        <figcaption className="mt-3 flex items-center gap-2 font-data text-[0.65rem] text-brand-muted uppercase tracking-[0.14em] leading-[1.5]">
          <span
            className="w-1.5 h-1.5 rounded-full bg-brand-meadow motion-safe:animate-pulse flex-shrink-0"
            aria-hidden="true"
          />
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
